import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { browserHistory } from 'react-router';
import ReactDOM from 'react-dom';
import styles from './Comparison.css';
import RiskFinderComponent from '../RiskFinderComponent';
import withStyles from '../../decorators/withStyles';
import { SetPlaceRanking, SetComparisonType } from '../../actions';
import { abbrevLower, commas, impactGroups, impactGroupsReverse, isUS, pplural, xlt } from "../../utils/utils";

const topojson = require('topojson');
const d3 = require('d3');
d3.tip = require('d3-tip').default;


class Comparison extends RiskFinderComponent {

  static propTypes = {
    waterLevel: PropTypes.object.isRequired,
    percentage: PropTypes.bool.isRequired,
    impacts: PropTypes.object,
    impact: PropTypes.string.isRequired,
    protection: PropTypes.string.isRequired,
    noData: PropTypes.func.isRequired,
    comparisonType: PropTypes.string.isRequired,
  };

  static defaultProps = {
    percentage: false,
    impact: "Population",
    protection: "tidelthresh",
    width: 400,
    height: 400,
    margin: {
      left: 10,
      right: 10,
      top: 10,
      bottom: 10,
    },
  };

  hasImpactData() {
    return Object.keys(this.getComparisonImpacts()).length > 0;
  }

  hasGeoData() {
    return Boolean(this.state.data.geoData);
  }

  async getGeoImpactData() {    
    const { placeType, placeID, comparisonType } = this.props;
    const comparisonID = placeType === comparisonType ? "peer" : comparisonType;

    var slrdb = "?slrdb=true";
    if (placeID.endsWith(".us")) {
      slrdb = "";
    }

    const urls = [
      '/api/geometry/' + placeType + '/' + placeID + '/' + comparisonID,
      '/api/impacts/' + placeType + '/' + placeID + '/' + comparisonID + slrdb,
    ];

    const results = await Promise.all(urls.map((url) => d3.json(url)));
    const [ geoData, impacts ] = results;

    return { geoData, impacts };
  }

  async loadData() {
    const self = this;
    const results = await this.getGeoImpactData();
    let callback;
    if (this.hasData() && this.hasGeoData()) {
      callback = () => { self.updateGeo(ReactDOM.findDOMNode(self)); };
    } else {
      callback = self.create;
    }
    this.setState({data: results}, callback);
  }

  componentWillUnmount() {
    var el = ReactDOM.findDOMNode(this).firstChild.firstChild;
    this.destroy(el);
  }

  componentDidUpdate(props) {
    this.updateMap(props);
  }

  async updateMap(props) {
    if (props.placeID !== this.props.placeID
        || props.placeType !== this.props.placeType
        || props.comparisonType !== this.props.comparisonType) {
      await this.loadData();
    } else if (props.waterLevel.level !== this.props.waterLevel.level ||
        props.waterLevel.unit !== this.props.waterLevel.unit ||
        props.impact !== this.props.impact ||
        props.percentage !== this.props.percentage ||
        props.protection !== this.props.protection) {
      this.update();
    }
  }

  updatePlaceRanking() {
    const { geoData, impacts } = this.state.data;
    const placeRanking = (geoData && impacts) ? this.getPlaceRanking(geoData, impacts) : [];
    this.props.dispatch(SetPlaceRanking(placeRanking));
  }

  validNavTarget(d) {
    const data = this.getPlaceImpacts(d);
    return data && data.impactCategoryValue && !this.props.noData(d);
  }

  navigateTo = d => {
    window.scrollTo(0, 0);
    browserHistory.push({pathname: `/${d.properties.placeType}/${d.properties.placeID}`});
  };

  getComparisonImpacts() {
    const { comparisonType, placeType, protection } = this.props;
    const data = this.state.data.impacts[protection];
    const comparison = comparisonType === 'peer' ? placeType : comparisonType;
    const filteredImpacts = Object.keys(data).filter((key) => key.split('/')[0] === comparison);
    const impactVals = filteredImpacts.reduce((obj, key) => {
      obj[key] = data[key];
      return obj;
    }, {});
    return impactVals;
  }

  getPlaceImpacts(d) {
    const { placeID, placeType } = this.props;
    const impactsData = this.getComparisonImpacts();
    return impactsData[`${d.properties.placeType}/${d.properties.placeID}`];
  }
        
  getPlaceRanking() {
    const { waterLevel, protection, impact, percentage, impactPretransform } = this.props;
    const { geoData } = this.state.data;
    const pretrans = impactPretransform ? val => impactPretransform(val) : val => val;
    const placeRankings = [];

    // only rank impacts for the current comparison type
    const impactVals = this.getComparisonImpacts();

    const value = obj => {
      try {
        var waterLevelVal = pretrans(obj[impact].water_level_values[waterLevel.unit][waterLevel.level]);
      } catch (err) {
        waterLevelVal = 0;
      }
      if (percentage) {
        if (waterLevelVal === 0 && obj[impact].total === 0) {
          return 0;
        } else {
          return waterLevelVal / pretrans(obj[impact].total) * 100;
        }
      } else {
        return waterLevelVal;
      }
    };
    const keys = Object.keys(impactVals).filter(k => impact in impactVals[k]);

    keys.sort((a, b) => {
      return value(impactVals[a]) > value(impactVals[b]) ? -1 : 1;
    });

    const numOrPct = obj => {
      return value(obj);
    };
    const geometries = geoData.objects[Object.keys(geoData.objects).filter(k => k !== "boundary")[0]].geometries;

    for (var i=0; i < 5; i++) {
      // It's OK if there aren't enough comparable places with a given impact.
      try {
        placeRankings.push({
          path: keys[i],
          name: geometries.filter(geom => `${geom.properties.placeType}/${geom.properties.placeID}` === keys[i])[0].properties.name,
          value: numOrPct(impactVals[keys[i]])
        });
      } catch (e) {}
    }

    return placeRankings;
  }

  isThisPlace(d) {
    return d.properties.placeID === this.props.placeID && d.properties.placeType === this.props.placeType;
  }

  create() {
    const element = ReactDOM.findDOMNode(this).firstChild.firstChild;
    var projection = d3.geoMercator()
      .scale(1)
      .translate([0, 0]);
    var path = d3.geoPath()
      .projection(projection);

    this.tip = d3.tip().attr("class", "d3-tip choropleth-tip").direction("e").offset([0, 5])
      .html(d => {
        const { waterLevel, impactPosttransform, protection, percentage } = this.props;
        const posttrans = impactPosttransform ? impactPosttransform : val => val;

        var data = this.getPlaceImpacts(d);

        if (!data || this.props.noData(d, data.impactCategoryValue)) {
          return `Data are not available for <var>${d.properties.name}</var>`;
        }

        if ((data && data.impactCategoryValue === 0)) {
          // if (this.props.noData(d)) {
          //   return `Data are not available for <var>${d.properties.name}</var>`;
          // } else {
            return `<var>${d.properties.name}</var> is not affected at <var>${waterLevel.level}</var><var>${waterLevel.unit}</var>`;
          // }
        }
        if (percentage) {
          var pct = data.impactCategoryValue;
          return `<span>In </span><span>${d.properties.name}</span><span>, </span><var>${pct.toFixed(1)}%</var><span> of </span>` +
          `<span>${abbrevLower(xlt(pplural(data.impactCategoryName)))}</span><span> are below </span><var>${waterLevel.level}</var><span>${waterLevel.unit}</span><span>. Select to navigate to its place page.</span>`;
        } else {
          const showDecimal = (data.impactCategoryValue > 0 && data.impactCategoryValue < 1);
          const roundedVal = showDecimal ? data.impactCategoryValue.toPrecision(1) : data.impactCategoryValue.toFixed(0);
          var valAtRisk = commas(posttrans(roundedVal));
          //
          // try {
          // } catch (err) {
          //   valAtRisk = 0;
          // }
          return `<span>In </span><span>${d.properties.name}</span><span>, </span><var>${valAtRisk}</var> ` +
          `<span>${abbrevLower(xlt(pplural(data.impactCategoryName)))}</span><span> are below </span><var>${waterLevel.level}</var><span>${waterLevel.unit}</span><span>. Select to navigate to its place page.</span>`;
        }
      });

    var svg = d3.select(element).append("svg")
      .attr("class", "choropleth-map")
      .attr("viewBox", "0 0 " + this.props.width + " " + this.props.height);

    svg.append("g")
      .attr("class", "choropleth-map-g")
      .append("path")
      .attr("d", path)
      .attr("class", "choropleth-mesh");

    svg.append("g")
      .attr("class", "choropleth-cities");

    svg.call(this.tip);

    var legend = d3.select(".ImpactChoropleth .legend-svg").append("svg")
      .attr("class", "choropleth-legend")
      .attr("height", "120px")
      .attr("viewBox", "0 0 100 60");

    legend.append("g")
      .attr("class", "boxes");

    legend.append("g")
      .attr("class", "labels");

    this.updateGeo(ReactDOM.findDOMNode(this));
  };

  updateGeo(element) {
    var self = this;
    var { geoData } = this.state.data;
    var projection = d3.geoMercator()
        .scale(1)
        .translate([0, 0]);
    var path = d3.geoPath()
        .projection(projection);

    var container;
    var points = {features: []};
    if ('City' in geoData.objects) {
      container = topojson.feature(geoData, geoData.objects.State);
      points = topojson.feature(geoData, geoData.objects.City);
    } else if ('heritage-city' in geoData.objects) {  // hack for Ecuador to show country boundary
      container = topojson.feature(geoData, geoData.objects.boundary);
      points = topojson.feature(geoData, geoData.objects['heritage-city']);
    } else if ('boundary' in geoData.objects) {
      container = topojson.feature(geoData, geoData.objects.boundary);
      Object.keys(geoData.objects).forEach(k => {
        if (k !== 'boundary') {
          if (geoData.objects[k].geometries[0].type === "Polygon" ||
              geoData.objects[k].geometries[0].type === "MultiPolygon") {
            container = topojson.feature(geoData, geoData.objects[k]);
          } else {
            points = topojson.feature(geoData, geoData.objects[k]);
          }
        }
      });
    } else {
      container = topojson.feature(geoData, geoData.objects[Object.keys(geoData.objects)[0]]);
    }

    // Scale and translate the projection to the bounding box of the container feature.
    // See https://bl.ocks.org/mbostock/4707858
    var width = this.props.width;
    var height = this.props.height;

    var b;
    if (this.props.choroplethBounds && (
          this.props.comparisonType === "county" ||
          this.props.comparisonType === "place" ||
          this.props.comparisonType === "congressional-district" ||
          this.props.comparisonType === "state-assembly-district" ||
          this.props.comparisonType === "state-senate-district" ||
          this.props.comparisonType === "metropolitan-planning-organization")) {
      var bounds = this.props.choroplethBounds.split(",");
      bounds = [bounds[1], bounds[2], bounds[3], bounds[0]].map(v => parseFloat(v));
      console.log(bounds);
      b = [projection(bounds.slice(0, 2)), projection(bounds.slice(2, 4))];
      console.log(b);
    } else {
      b = path.bounds(container);
    }
    var  s = .95 / Math.max((b[1][0] - b[0][0]) / width, (b[1][1] - b[0][1]) / height),
      t = [(width - s * (b[1][0] + b[0][0])) / 2, (height - s * (b[1][1] + b[0][1])) / 2];
    const aspect = Math.abs((b[0][0]) - b[0][1]) / Math.abs((b[1][0] - b[1][1]));
    d3.select(element).selectAll(".choropleth-map").attr("viewBox", function() {
      if (aspect > 1) {
        const val = 400 / aspect;
        return "0 " + ((400 - val) / 2).toString() + " 400 " + val.toString();
      } else {
        const val = 400 / aspect;
        return ((400 - val) / 2).toString() + " 0 " + val.toString() + " 400";
      }
    }());
    projection.scale(s).translate(t);

    var paths = d3.select(element).selectAll(".choropleth-map-g").selectAll(".choropleth-mesh")
      .data(container.features);

    paths
      .attr("d", path)
      .attr("class", d => "choropleth-mesh" + (points.features.length === 0 ? " active" : ""))
      .on("mouseover", function(d) {
        if (points.features.length === 0) {
          d3.select(this)
            .style("stroke", "black")
            .style("stroke-width", 1);
          self.tip.show(d, this);
        }
      })
      .on("mouseout", function(d) {
        if (points.features.length === 0) {
          d3.select(this)
            .style("stroke", self.isThisPlace(d) ? "#027bbf" : "#4d4d4d")
            .style("stroke-width", self.isThisPlace(d) ? 2 : 0.5);
          self.tip.hide()
        }
      })
      .on("click", (d) => {
        if (points.features.length === 0 && self.validNavTarget(d)) {
          this.navigateTo(d);
        }
      });

    paths.enter().append("path")
      .attr("d", path)
      .attr("class", d => "choropleth-mesh" + (points.features.length === 0 ? " active" : ""))
      .on("mouseover", function(d) {
        if (points.features.length === 0) {
          d3.select(this)
            .style("stroke", "black")
            .style("stroke-width", 1);
          self.tip.show(d, this);
        }
      })
      .on("mouseout", function(d) {
        if (points.features.length === 0) {
          d3.select(this)
            .style("stroke", self.isThisPlace(d) ? "#027bbf" : "#4d4d4d")
            .style("stroke-width", self.isThisPlace(d) ? 2 : 0.5);
          self.tip.hide()
        }
      })
      .on("click", (d) => {
        if (points.features.length === 0 && self.validNavTarget(d)) {
          this.navigateTo(d);
        }
      });

    paths.exit().remove();

    var circles = d3.select(element).selectAll(".choropleth-cities").selectAll("circle")
      .data(points.features);

    circles
      .attr("cx", d => projection(d.geometry.coordinates)[0])
      .attr("cy", d => projection(d.geometry.coordinates)[1])
      .attr('r', '5')
      .on("mouseover", function(d) {
        d3.select(this)
          .style("stroke", "black")
          .style("stroke-width", 1);
        self.tip.show(d, this);
      })
      .on("mouseout", function(d) {
        d3.select(this)
          .style("stroke", self.isThisPlace(d) ? "#027bbf" : "#4d4d4d")
          .style("stroke-width", self.isThisPlace(d) ? 2 : 0.5);
        self.tip.hide();
      })
      .on("click", (d) => {this.validNavTarget(d) && this.navigateTo(d)});

    circles
      .enter().append("circle")
      .attr("cx", d => projection(d.geometry.coordinates)[0])
      .attr("cy", d => projection(d.geometry.coordinates)[1])
      .attr('r', '5')
      .on("mouseover", function(d) {
        d3.select(this)
          .style("stroke", "black")
          .style("stroke-width", 1);
        self.tip.show(d, this);
      })
      .on("mouseout", function(d) {
        d3.select(this)
          .style("stroke", self.isThisPlace(d) ? "#027bbf" : "#4d4d4d")
          .style("stroke-width", self.isThisPlace(d) ? 2 : 0.5);
        self.tip.hide();
      })
      .on("click", (d) => {this.validNavTarget(d) && this.navigateTo(d)});

    circles.exit().remove();

    this.update();
  };

  getDigits(num) {
    return parseInt(num.toExponential().match(/\+([0-9]+)/)[1])
  }

  round(min, max) {
    if (this.props.percentage) {
      return x => x;
    } else {
      try {
        const maxDigits = this.getDigits(max);
        return x => {
          try {
            if (this.getDigits(x) < maxDigits) {
              return parseFloat(x.toPrecision(1));
            } else {
              return parseFloat(x.toPrecision(2));
            }
          } catch (err) {
            return x;
          }
        }
      } catch (err) {
        return x => x;
      }
    }
  }

  updateLegend(color, min, max, allValues) {

    const round = this.round(min, max);
    const roundValues = allValues.map(round);
    roundValues.sort((a, b) => a < b ? 1 : -1);
    var seenVals = new Set();
    var values = [];
    roundValues.forEach(val => {
      const col = color(val);
      if (!seenVals.has(col) && !isNaN(val)) {
        values.push(val);
      }
      seenVals.add(col);
    });
    // Add a "sentinel" value to represent "not affected".
    values.push(-1);

    const self = this;
    const textOffset = this.props.impactPosttransform ? 75: 55;
    const textTransform = function(d) {
      if (d === null || d === -1 || isNaN(d)) {
        return "0";
      }
      if (self.props.percentage) {
        return commas(round(d).toFixed(0)) + (self.props.percentage ? "%" : "");
      } else {
        if (self.props.impactPosttransform) {
          return commas(self.props.impactPosttransform(round(d).toFixed(0)));
        } else {
          return commas(round(d).toFixed(0));
        }
      }
    };

    const translateUnit = text => {
      const textElems = text.split(" ");
      return `${textElems[0]} ${xlt(textElems.slice(1).join(" "))}`;
    };

    var legend = d3.selectAll(".ImpactChoropleth .boxes")
      .selectAll("g").attr("class", "legend-box-g").data(values);

    legend.select("rect").attr("fill", d => d === -1 ? "#fff" : color(round(d)));
    legend.select("text")
      .attr("x", textOffset)
      .text(d => translateUnit(textTransform(d)));

    var legendG = legend.enter().append("g");

    legendG
      .append("rect")
      .attr("x", 10)
      .attr("y", (d, i) => (10 * i))
      .attr("width", 10)
      .attr("height", 10)
      .attr("fill", d => d === -1 ? "#fff" : color(round(d)));

    legendG
      .append("text")
      .attr("class", "notranslate")
      .attr("x", textOffset)
      .attr("y", (d, i) => 8 + (10 * i))
      .attr("text-anchor", "end")
      .text(d => translateUnit(textTransform(d)));

    legend.exit().remove();
  }

  /**
   * Returns data corresponding to a given exposure ID. If data associated with the preferred
   * exposure ID is unavailable, searches for a different available data source defined as having the same
   * meaning, using utils.impactGroups and utils.impactGroupsReverse.
   * @param initialImpact: the preferrred exposure ID to use.
   * @param obj: an object whose keys defined data available for certain impact IDs,
   *  and whose values may have any structure.
   * @returns {*}
   */
  findSuitableImpact(initialImpact, obj) {
    var choroImpact = initialImpact;
    if (!(choroImpact in obj)) {
      if (choroImpact in impactGroupsReverse) {
        choroImpact = impactGroups[impactGroupsReverse[choroImpact]].find(alternative => alternative in obj);
        if (!choroImpact) {
          return null;
        }
      }
    }
    return obj[choroImpact];
  }

  getMapValues(data) {
    const { impact, waterLevel, impactPretransform, percentage } = this.props;
    var values;
    const pretrans = impactPretransform ? impactPretransform : val => val;
    if (percentage) {
      values = Object.values(data).map(d => {
        const impactData = this.findSuitableImpact(impact, d);
        try {
          d.impactCategoryValue = pretrans(impactData.water_level_values[waterLevel.unit][waterLevel.level]) / impactData.total * 100;
          d.impactCategoryName = impactData.name;
          return d.impactCategoryValue;
        } catch (err) {
          d.impactCategoryValue = null;
          d.impactCategoryName = null;
          return null;
        }
      });
    } else {
      values = Object.values(data).map(d => {
        const impactData = this.findSuitableImpact(impact, d);
        try {
          d.impactCategoryValue = pretrans(impactData.water_level_values[waterLevel.unit][waterLevel.level]);
          d.impactCategoryName = impactData.name;
          return d.impactCategoryValue;
        } catch (err) {
          d.impactCategoryValue = null;
          d.impactCategoryName = null;
          return null;
        }
      });
    }
    return values;
  }

  updateChoro = element => {
    const self = this;
    const { percentage } = this.props;

    if (!this.state.data.impacts) {
      console.error(`No impact data found for ${protection}`);
      return;
    }

    const data = this.getComparisonImpacts();
    const values = this.getMapValues(data);

    const min = d3.min(values);
    const max = d3.max(values);

    const round = this.round(min, max);
    const color = d3.scaleQuantize()
      .domain([min, max + 0.00001])
      .range(percentage ?
        ["#ecf5fa", "#b6c5d3", "#8197ae", "#4c6c8a", "#0B4467"] :
        ["#f7eee2", "#e8d1af", "#d6b47e", "#c1994d", "#ab7e15"]
      );

    this.updateLegend(color, min, max, values);

    var points = d3.select(element).selectAll(".choropleth-cities").selectAll("circle");
    var elements;
    if (points.nodes().length > 0) {
      elements = points;
      d3.select(element).selectAll(".choropleth-mesh")
        .style("fill", "#fff")
        .style("stroke-width", 0.5)
        .style("stroke", "#4d4d4d");
    } else {
      elements = d3.select(element).selectAll(".choropleth-mesh");
    }

    elements
      .style("stroke-width", d => self.isThisPlace(d) ? 2 : 0.5)
      .style("stroke", d => {
        return self.isThisPlace(d) ? "#027bbf" : "#4d4d4d";
      })
      .style("fill", d => {
        if (this.props.noData(d)) {
          return "#ddd"
        }
        try {
          var valAtWaterLevel = data[`${d.properties.placeType}/${d.properties.placeID}`].impactCategoryValue;
          if (this.props.noData(d, valAtWaterLevel)) {
            return "#ddd"
          } else if (valAtWaterLevel === 0) {
            return "#fff"
          }
        } catch (err) {
          return "#fff"
        }

        return color(round(valAtWaterLevel));
      });
  };

  update = () => {
    if (this.hasImpactData() && this.hasGeoData()) {
      const element = ReactDOM.findDOMNode(this).firstChild.firstChild;
      this.updateChoro(element);
      this.updatePlaceRanking();      
    }
  };

  destroy(element) {
    // TODO
  };

  render() {
    const impactName = (!isUS(this.props.placeID) && this.props.impactName === "Square miles of land" ?
      "Square km of land" : this.props.impactName);
    const unitName = this.props.percentage ? `% ${xlt(impactName)}` : xlt(impactName);
    return <div className="ImpactChoropleth Choropleth">
      <div className="Choropleth-map"><div className="Choropleth-map-container"></div></div>
      <div className="Choropleth-legend">
        <h4 key={Math.random().toString()}>{unitName}</h4>
        <div className="legend-svg"></div>
        <span className="small-text">Legend values are bin upper limits</span>
      </div>
    </div>;
  }

}

export default compose(
  withStyles(styles),
  connect((state) => {return {isEmbed: state.isEmbed, choroplethBounds: state.choroplethBounds}}),
)(Comparison);
